import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Ruler, Plus, Pencil, Trash2, Search, CheckCircle2, XCircle, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

export default function UOMMasterPage() {
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUom, setEditingUom] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    is_active: true
  });

  const fetchUoms = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/uom?active_only=${!showInactive}`, {
        headers: getAuthHeader()
      });
      setUoms(response.data.uoms || []);
    } catch (error) {
      console.error('Error fetching UOMs:', error);
      toast.error('Failed to fetch UOMs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUoms();
  }, [showInactive]);

  const openCreateDialog = () => {
    setEditingUom(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      is_active: true
    });
    setDialogOpen(true);
  };

  const openEditDialog = (uom) => {
    setEditingUom(uom);
    setFormData({
      code: uom.code,
      name: uom.name,
      description: uom.description || '',
      is_active: uom.is_active
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code.trim()) {
      toast.error('Code is required');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingUom) {
        // Update
        await axios.patch(`${API_URL}/api/uom/${editingUom.id}`, {
          name: formData.name,
          description: formData.description,
          is_active: formData.is_active
        }, { headers: getAuthHeader() });
        toast.success('UOM updated successfully');
      } else {
        // Create
        await axios.post(`${API_URL}/api/uom`, formData, { headers: getAuthHeader() });
        toast.success('UOM created successfully');
      }
      setDialogOpen(false);
      fetchUoms();
    } catch (error) {
      console.error('Error saving UOM:', error);
      toast.error(error.response?.data?.detail || 'Failed to save UOM');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (uom) => {
    if (uom.is_system) {
      toast.error('System UOMs cannot be deleted. You can deactivate them instead.');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete UOM "${uom.code}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/uom/${uom.id}`, { headers: getAuthHeader() });
      toast.success('UOM deleted successfully');
      fetchUoms();
    } catch (error) {
      console.error('Error deleting UOM:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete UOM');
    }
  };

  const toggleActive = async (uom) => {
    try {
      await axios.patch(`${API_URL}/api/uom/${uom.id}`, {
        is_active: !uom.is_active
      }, { headers: getAuthHeader() });
      toast.success(`UOM ${uom.is_active ? 'deactivated' : 'activated'}`);
      fetchUoms();
    } catch (error) {
      console.error('Error toggling UOM status:', error);
      toast.error('Failed to update UOM status');
    }
  };

  // Filter UOMs based on search
  const filteredUoms = uoms.filter(uom => {
    const searchLower = searchTerm.toLowerCase();
    return (
      uom.code.toLowerCase().includes(searchLower) ||
      uom.name.toLowerCase().includes(searchLower) ||
      (uom.description && uom.description.toLowerCase().includes(searchLower))
    );
  });

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="uom-master-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Ruler className="w-6 h-6" />
              UOM Master
            </h1>
            <p className="text-muted-foreground">Manage units of measurement used across the system</p>
          </div>
          <Button onClick={openCreateDialog} data-testid="add-uom-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add UOM
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by code, name, or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="uom-search"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="show-inactive"
                  checked={showInactive}
                  onCheckedChange={setShowInactive}
                />
                <Label htmlFor="show-inactive" className="text-sm">Show Inactive</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* UOM List */}
        <Card>
          <CardHeader>
            <CardTitle>Units of Measurement</CardTitle>
            <CardDescription>
              {filteredUoms.length} UOM{filteredUoms.length !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : filteredUoms.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchTerm ? 'No UOMs match your search' : 'No UOMs found'}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-24 text-center">Status</TableHead>
                    <TableHead className="w-24 text-center">Type</TableHead>
                    <TableHead className="w-32 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUoms.map((uom) => (
                    <TableRow key={uom.id} className={!uom.is_active ? 'opacity-50' : ''}>
                      <TableCell className="font-mono font-bold">{uom.code}</TableCell>
                      <TableCell>{uom.name}</TableCell>
                      <TableCell className="text-muted-foreground">{uom.description || '-'}</TableCell>
                      <TableCell className="text-center">
                        {uom.is_active ? (
                          <Badge variant="outline" className="text-green-600 border-green-300">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-500 border-gray-300">
                            <XCircle className="w-3 h-3 mr-1" /> Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {uom.is_system ? (
                          <Badge variant="secondary" className="text-xs">
                            <Lock className="w-3 h-3 mr-1" /> System
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">Custom</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(uom)}
                            data-testid={`edit-uom-${uom.code}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleActive(uom)}
                            title={uom.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {uom.is_active ? (
                              <XCircle className="w-4 h-4 text-orange-500" />
                            ) : (
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                            )}
                          </Button>
                          {!uom.is_system && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(uom)}
                              className="text-red-500 hover:text-red-700"
                              data-testid={`delete-uom-${uom.code}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingUom ? 'Edit UOM' : 'Add New UOM'}</DialogTitle>
              <DialogDescription>
                {editingUom ? 'Update the unit of measurement details' : 'Create a new unit of measurement'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="code">Code *</Label>
                <Input
                  id="code"
                  placeholder="e.g., KG, MTR, NOS"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  disabled={editingUom !== null}
                  maxLength={10}
                  data-testid="uom-code-input"
                />
                {editingUom && (
                  <p className="text-xs text-muted-foreground">Code cannot be changed after creation</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Kilograms, Meters, Numbers"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="uom-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Optional description..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-uom-btn">
                {saving ? 'Saving...' : (editingUom ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
